import { useCallback } from 'react';

interface UseClipboardOptions {
	successCallback?: () => void;
	errorCallback?: () => void;
	logError?: (error: unknown) => void;
}

export const useClipboard = ({
	successCallback,
	errorCallback,
	logError = (error: unknown) => console.log('[Clipboard] Error:', error),
}: UseClipboardOptions = {}) => {
	const copyWithFallback = useCallback(
		async (text: string): Promise<boolean> => {
			try {
				// Create temporary textarea element
				const textArea = document.createElement('textarea');
				textArea.value = text;

				// Avoid scrolling to bottom
				textArea.style.top = '0';
				textArea.style.left = '0';
				textArea.style.position = 'fixed';
				textArea.style.opacity = '0';

				document.body.appendChild(textArea);
				textArea.focus();
				textArea.select();

				const successful = document.execCommand('copy');
				document.body.removeChild(textArea);

				return successful;
			} catch (error) {
				logError(error);
				return false;
			}
		},
		[logError],
	);

	const copyToClipboard = useCallback(
		async (text: string): Promise<boolean> => {
			try {
				if (!navigator.clipboard) {
					logError('Clipboard API not available, using fallback');
					const fallbackSuccess = await copyWithFallback(text);
					if (fallbackSuccess) {
						successCallback?.();
						return true;
					}
					throw new Error('Fallback copy failed');
				}

				await navigator.clipboard.writeText(text);
				successCallback?.();
				return true;
			} catch (error) {
				logError(error);
				errorCallback?.();
				return false;
			}
		},
		[copyWithFallback, successCallback, errorCallback, logError],
	);

	return { copyToClipboard };
};
